import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import { Box, Checkbox, FormControlLabel, TextField } from "@mui/material";
import { observer } from "mobx-react-lite";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { USER_SETTINGS } from "../../../constants/UserConstants.ts";
import { LeadSourceEnum } from "../../../constants/common.ts";
import { useAppStore } from "../../../hooks/useStores.tsx";
import BriaButton from "../../common/BriaButton/BriaButton.tsx";
import ConfirmationPopup from "../../common/ConfirmationPopup/ConfirmationPopup.tsx";
import styles from "./LeadSourcePopup.module.scss";
interface LeadSourcePopupProps {
	open?: boolean;
	onClose?: () => void;
}

const LeadSourcePopup = ({ open, onClose: onCloseProp }: LeadSourcePopupProps = {}) => {
	const { uiStore, authStore } = useAppStore();
	const { t } = useTranslation("translation", { keyPrefix: "leadSourcePopup" });
	const [selectedSource, setSelectedSource] = useState<LeadSourceEnum | null>(null);
	const [otherDetails, setOtherDetails] = useState<string>("");
	const [isSubmitting, setIsSubmitting] = useState(false);

	const onClose = async () => {
		await authStore.user?.updateSettings(USER_SETTINGS.hideLeadSourcePopup, true);

		if (onCloseProp) {
			onCloseProp();
		} else {
			uiStore.hideDialog("leadSourcePopup");
		}
		setSelectedSource(null);
		setOtherDetails("");
	};

	const handleSourceChange = (source: LeadSourceEnum) => {
		setSelectedSource(source);
		if (source !== LeadSourceEnum.OTHER) {
			setOtherDetails("");
		}
	};

	const handleSubmit = async () => {
		if (!selectedSource) return;

		setIsSubmitting(true);
		try {
			await authStore.user?.updateLeadSource(selectedSource, otherDetails);
			onClose();
		} catch (error) {
			console.error("Error saving lead source:", error);
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleSkip = () => {
		onClose();
	};

	const isSubmitDisabled = !selectedSource || (selectedSource === LeadSourceEnum.OTHER && !otherDetails.trim());

	const leadSourceOptions = [
		{ value: LeadSourceEnum.GOOGLE_SEARCH, label: t("options.googleSearch") },
		{ value: LeadSourceEnum.SOCIAL_MEDIA, label: t("options.socialMedia") },
		{ value: LeadSourceEnum.HUGGING_FACE, label: t("options.huggingFace") },
		{ value: LeadSourceEnum.GITHUB, label: t("options.github") },
		{ value: LeadSourceEnum.EVENT_CONFERENCE, label: t("options.eventConference") },
		{ value: LeadSourceEnum.OTHER, label: t("options.other") },
	];

	return (
		<ConfirmationPopup
			open={open !== undefined ? open : uiStore.leadSourcePopup}
			onClose={onClose}
			title={t("title")}
			hideActions={true}
			hideCloseIcon={true}
			paperClassName={styles.leadSourcePaper}
		>
			<Box className={styles.content}>
				<Box className={styles.optionsContainer}>
					{leadSourceOptions.map((option) => (
						<FormControlLabel
							key={option.value}
							control={
								<Checkbox
									checked={selectedSource === option.value}
									onChange={() => handleSourceChange(option.value)}
									disableRipple
									className={styles.checkbox}
									icon={<RadioButtonUncheckedIcon />}
									checkedIcon={<RadioButtonCheckedIcon />}
									sx={{
										"& .MuiSvgIcon-root": {
											fontSize: "20px",
										},
									}}
								/>
							}
							label={option.label}
							className={styles.option}
						/>
					))}
				</Box>

				{selectedSource === LeadSourceEnum.OTHER && (
					<TextField
						value={otherDetails}
						onChange={(e) => setOtherDetails(e.target.value)}
						placeholder={t("otherPlaceholder")}
						className={styles.otherTextField}
						multiline
						rows={2}
						variant="outlined"
					/>
				)}

				<Box className={styles.buttonsContainer}>
					<BriaButton onClick={handleSkip} buttonType="textSmall" className={styles.skipButton}>
						{t("skip")}
					</BriaButton>
					<BriaButton
						onClick={handleSubmit}
						buttonType="primaryMedium"
						className={styles.submitButton}
						disabled={isSubmitDisabled}
						loading={isSubmitting}
					>
						{t("submit")}
					</BriaButton>
				</Box>
			</Box>
		</ConfirmationPopup>
	);
};

export default observer(LeadSourcePopup);
